import { prepareOfferShareText } from '@proffer/shared'
import type { OfferDto } from '../types/offer'

export type ShareBusinessContext = {
  businessName?: string | null
  vatNumber?: string | null
  address?: string | null
  phone?: string | null
  business_info?: string | null
}

/** Κείμενο για WhatsApp / Viber / email — μόνο το κείμενο προσφοράς. */
export function buildOfferShareText(
  offer: OfferDto,
  _user?: ShareBusinessContext | null,
): string {
  const body = offer.generated_text?.trim()
  if (body) return prepareOfferShareText(body, offer.amount)
  return offer.raw_notes?.trim() || offer.title
}

/**
 * Περιορίζει το κείμενο ώστε το `encodeURIComponent(text)` να μην ξεπερνά το όριο.
 * Πολύ μεγάλα query strings κόβονται από browser/Viber στη μέση UTF-8 → «????» και �.
 */
export function truncateTextForEncodedQuery(
  text: string,
  maxEncodedLength: number,
): string {
  if (encodeURIComponent(text).length <= maxEncodedLength) return text
  const suffix = '\n\n[…πλήρες κείμενο στην εφαρμογή ProfferAI]'
  const suffixLen = encodeURIComponent(suffix).length
  const budget = maxEncodedLength - suffixLen

  const maxPrefixUnder = (encodedBudget: number): number => {
    let lo = 0
    let hi = text.length
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2)
      if (encodeURIComponent(text.slice(0, mid)).length <= encodedBudget)
        lo = mid
      else hi = mid - 1
    }
    return lo
  }

  if (budget >= 8) {
    return text.slice(0, maxPrefixUnder(budget)) + suffix
  }
  return text.slice(0, maxPrefixUnder(maxEncodedLength))
}

/** Πρόθεμα πριν το `encodeURIComponent(κείμενο)` — το συνολικό URL πρέπει να μένει κάτω από αυστηρά όρια. */
export const WA_ME_URL_PREFIX = 'https://wa.me/?text='
export const VIBER_FORWARD_URL_PREFIX = 'viber://forward?text='

/**
 * Συνολικό μέγιστο μήκος του href (prefix + encodeURIComponent(text)).
 * Ορισμένα WebView / WhatsApp / Viber κόβουν ~1–2KB και σπάνε UTF-8 στη μέση → «????».
 * Το κωδικοποιημένο κείμενο υπολογίζεται ως budget − μήκος prefix.
 */
export const SHARE_URL_TOTAL_BUDGET = 1024

export function truncateForShareUrl(text: string, urlPrefix: string): string {
  const maxEncoded = Math.max(64, SHARE_URL_TOTAL_BUDGET - urlPrefix.length)
  return truncateTextForEncodedQuery(text, maxEncoded)
}

export function offerPdfFilename(offer: OfferDto): string {
  const safe =
    offer.title
      .replace(/[^a-zA-Z0-9\u0370-\u03FF\u1F00-\u1FFF]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'prosfora'
  return `${safe}-${offer.id.slice(0, 8)}.pdf`
}
